import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import hash from "hash-it";
import "./AssessmentModal.scss";
import { addBatchAssignments } from "../../../../services/assignment/addBatchAssignments";
import DomainIcon from "./DomainIcon";
import { ASSIGNMENT_TYPE } from "../../../../services/assignment/constants";
import manifestHelper from "../../../../services/assignment/manifest";
import SIS from "../../../../services/StudentInformationService";
import { useTranslation } from "react-i18next";
import { getAssessmentConfig } from "../../../../services/amiraLicenseService";
import { findSelectedAssessmentTagObjectFromSavedTags } from "../../../../components/Teacher/Admin/containers/SchoolConfigurations/SelectAssessmentTags";
import { CONFIGURATION_DATA, DYSLEXIA_TASKS } from "../../../../constants/assessmentConfigPageConstants";
import { getReport } from "../../../../components/Reports/services/tracking";
import { StudentListModalPeriodRibbon } from "../StudentListModal/StudentListModalPeriodRibbon";
import "../StudentListModal/StudentListModal.scss";
import filterOutline from "@/images/icons/filter-outline.svg";
import { Dropdown } from "../../../../components/Global/Dropdown";
import { StudentAvatar } from "../StudentAvatar/StudentAvatar";
import { ButtonThemed } from "@/components/shared/ButtonThemed";
import closeButton from "@/images/icons/close-modal-button.svg";
import checkmark from "@/images/icons/schedule-checkmark.svg";
import { Student } from "@/components/Global/DonutChart/DonutChart";
import { Placeholder } from "@/components/Global/Placeholder/Placeholder";
import { makeToast, ToastTypes } from '@/components/Global/Notifications';
import { AppDispatch } from "@/models/AppDispatch.type";
import { fetchAssessmentStatusWidgetData } from "../../store/slices/assessmentStatusWidgetSlice";
import closeModalIcon  from "@/images/icons/close-modal-button.svg";
import TestingWindow from "../StudentListModal/TestingWindow";
import { AssessmentWindow } from "../../utils/findAssessmentWindow";
import StudentListErrorMessage from "../StudentListModal/StudentListErrorMessage";
import { useCalendar } from "@/hooks/useCalendar";
import { AssessmentPeriodItem, AssessmentPeriodWindowType, useAssessmentPeriodWindows } from "@/hooks/useAssessmentPeriods";
import { SelectedPeriodCallback } from '../StudentListModal/TestingWindow';

type TaskType = keyof typeof taskLookup;

interface AssessmentTagObject {
  override?: Record<string, any>;
}

function Controls({ waiting, createAssignments, t, handleCancelScheduling }: { waiting: boolean, createAssignments: () => void, t: any, handleCancelScheduling: () => void }) {
  return (
    <div className="controls">
      <button className="cancel-button" onClick={handleCancelScheduling} data-testid="cancel-button">
        <img src={closeButton} alt="close" className="cancel-button-icon" /> <span className="cancel-button-text">Cancel</span>
      </button>
      <ButtonThemed
        variant="dark"
        className="create-button button-with-icon btn-sm create-button"
        onClick={createAssignments}
        disabled={waiting}
      >
         <span className="d-flex flex-nowrap align-items-center">
          <img src={checkmark} role="presentation" className="checkmark-icon"/>
            {t("common:Create")}
        </span>
      </ButtonThemed>
    </div>
  );
}

export enum AssignmentType {
  PROGRESS_MONITORING = "PROGRESS_MONITORING",
  BENCHMARK = "BENCHMARK",
}

interface ManifestItem {
  activityType: string;
  locale: string;
  sequence: string;
}

interface Template {
  assignmentType: string;
  manifest: ManifestItem[];
}

interface AssessmentData {
  templates: Template[];
  windows: any[];
  hasFutureWindow: boolean;
  activeWindow: any;
}

export interface StudentData {
  studentId: string;
  first_name: string;
  last_name: string;
  metadata: any;
  manifest: any[];
  grade: number;
  edited?: boolean;
}

interface StudentDataWithAssignmentType extends StudentData {
  assignmentType: string;
}

interface StudentManifestConfig {
  [key: string]: ManifestConfig
}
interface ManifestConfig {
  assessOn: string;
  sessions: number;
  instructionsLanguage: string;
}

interface SelectedWindow {
  startDate: string | null;
  endDate: string | null;
  value: number | null;
  screeningWindowType: string | null;
  assignmentId: string | null;
  assignmentType: AssignmentType | null;
  filterType?: 'BENCHMARK' | 'PROGRESS_MONITOR' | 'PERIOD';
  benchmarkTag?: 'BOY' | 'MOY' | 'EOY';
}

interface AssessmentModalProps {
  districtId: string;
  schoolId: string;
  selectedStudents: { [key: string]: Student };
  scheduleErrorMessage: string | null;
  onCloseHandler: () => void;
  classroomId: number;
  handleCancelScheduling: () => void;
  hasActive: boolean;
  assessmentWindowStatus: AssessmentWindow;
  currentDistrictWindow?: AssessmentPeriodItem;
  controlledSelectedWindow?: SelectedWindow;
  onSelectedPeriodChange?: (selectedOption: SelectedPeriodCallback) => void;
  previousDistrictWindow?: boolean;
}

export default function AssessmentModal({
  districtId,
  schoolId,
  selectedStudents,
  scheduleErrorMessage,
  onCloseHandler,
  classroomId,
  handleCancelScheduling,
  hasActive,
  assessmentWindowStatus,
  currentDistrictWindow,
  controlledSelectedWindow,
  onSelectedPeriodChange,
  previousDistrictWindow
}: AssessmentModalProps) {
  const students = useSelector((state: any) => state.reports.roster.data.student);
  const assignor = useSelector((state: any) => state.userData.userId);
  const [reportData, setReportData] = useState<AssessmentData[] | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [contentTags, setContentTags] = useState(null);
  const [assignmentType, setAssignmentType] = useState<AssignmentType>(
    !hasActive ? AssignmentType.PROGRESS_MONITORING : (controlledSelectedWindow?.assignmentType as AssignmentType)
  );
  const [studentManifestData, setStudentManifestData] = useState<StudentData[]>([]);
  const [studentManifestConfig, setStudentManifestConfig] = useState<StudentManifestConfig>({});
  const [freshStudentData, setFreshStudentData] = useState<any[]>([]);
  const dispatch = useDispatch<AppDispatch>();

  const { assessmentCalendar } = useCalendar();
  const {t} = useTranslation(["tracking_report", "assessment_modal"])
  const currentSchoolYear = assessmentCalendar?.schoolYear;


  useEffect(() => {
    if (classroomId) {
      SIS.getClassroomStudents(classroomId).then((freshStudents) => {
        setFreshStudentData(freshStudents);
      }).catch((error) => {
        console.error('Error fetching fresh student data:', error);

        setFreshStudentData(students);
      });
    }
  }, [classroomId]);

  useEffect(() => {
    if(districtId && schoolId){
      getAssessmentConfig(districtId, schoolId).then((config) => {
        setContentTags(config?.contentTags);
      });
    }
  }, [districtId, schoolId]);

  useEffect(() => {
    if(schoolId && districtId){
      getReport(schoolId, districtId).then((report) => {
        if (report) {
          setReportData([report]);
        } else {
          setReportData(null);
        }
      });
    }
  }, [schoolId, districtId]);

  useEffect(() => {
    if (reportData && reportData.length > 0 && freshStudentData.length > 0) {

      const filteredStudents = freshStudentData.filter((student: any) => selectedStudents[student.id]);

      const newStudentManifestData: StudentData[] = [];
      const newStudentManifestConfig: StudentManifestConfig = {};

      filteredStudents.forEach((student: any) => {
        let studentGrade;
        
        if (student.grade === -1) {
          studentGrade = 9; // PRE_K is at index 9 (the last template)
        } else {
          studentGrade = student.grade;
        }
        
        const template = reportData[0]?.templates[studentGrade];
        const manifest = template ? [...template.manifest] : [];

        const studentData: StudentData = {
          studentId: student.id,
          first_name: student.first_name,
          last_name: student.last_name,
          metadata: { ...student.metadata },
          manifest: manifest,
          grade: studentGrade,
        };

        newStudentManifestData.push(studentData);

        const locales = new Set(manifest.map((m: any) => m.locale));
        const assessOn = Array.from(locales).join(', ');
        const sessions = manifest.some((m: any) => m.sequence === "BREAK" && manifest.indexOf(m) < manifest.length - 1) ? 2 : 1;


        const instructionsLanguage = student.metadata?.tutorInSpanish ? "ES_MX" : "EN_US";

        newStudentManifestConfig[student.id] = {
          assessOn,
          sessions,
          instructionsLanguage,
        };
      });

      setStudentManifestData(newStudentManifestData);
      setStudentManifestConfig(newStudentManifestConfig);
    }
  }, [reportData, freshStudentData, selectedStudents]);

  const handleSpanishBenchmarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setAssignmentType(AssignmentType.BENCHMARK);
    } else {
      setAssignmentType(AssignmentType.PROGRESS_MONITORING);
    }
  };

  return (
    <>
    <div className="reports-container-assessment-modal assignment" data-testid="schedule-assessment-modal">
      <div className="scroll-content">
        <div className="assessment-modal-header-container">
          <div className="assessment-modal-header-close d-flex justify-content-between align-items-center">
            <div className="assessment-modal-header">
              {t("assessment_modal:schedule_assessment")}
            </div>
            <button className="assessment-modal-close-button" onClick={onCloseHandler} data-testid="close-button">
              <img src={closeModalIcon} alt="close" className="assessment-modal-close-button-icon"/>
            </button>
          </div>
          <div className="assessment-modal-description">
            {t("assessment_modal:on_demand_assessments_are_given_to_students_upon_their_next_login_and_expire_at_the_start_of_the_next_assessment_window")}
          </div>
          <StudentListErrorMessage scheduleErrorMessage={scheduleErrorMessage} />
          <div className="d-flex align-items-center w-100 mb-3" style={{ justifyContent: 'space-between' }}>
              <div style={{ flex: '0 0 auto' }}>
                <TestingWindow
                  assessmentModal={true}
                  assessmentWindowStatus={assessmentWindowStatus}
                  currentDistrictWindow={currentDistrictWindow}
                  controlledSelectedWindow={controlledSelectedWindow}
                  onSelectedPeriodChange={onSelectedPeriodChange}
                />
              </div>
               {!hasActive && (
                <div className="period-ribbon-assessment-alert d-flex flex-column align-items-start" style={{ flex: '0 0 auto' }}>
                  <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      id="spanishBenchmark"
                      className="me-2"
                      onChange={handleSpanishBenchmarkChange}
                      checked={assignmentType === AssignmentType.BENCHMARK}
                      disabled={false}
                    />
                    <span className="period-ribbon-assessment-alert-text" style={{ color: '#262626', fontSize: '1.125rem', fontWeight: '600',}}>{t('assessment_modal:assessment_benchmark_warning')}</span>
                  </div>
                  <div className="d-flex flex-column">
                    <span className="period-ribbon-assessment-alert-text" style={{ color: '#262626'}}>{t('assessment_modal:assessment_benchmark_warning_description')}</span>
                  </div>
                </div>
              )}
            </div>
        </div>
        <StudentList
          students={studentManifestData}
          studentManifestConfig={studentManifestConfig}
          setStudentManifestConfig={setStudentManifestConfig}
          selectedStudents={[selectedStudents]}
          />
      </div>
      <Controls
        waiting={waiting}
        createAssignments={() =>
          createAssignments(
            studentManifestConfig,
            studentManifestData,
            assignmentType,
            assignor,
            onCloseHandler,
            dispatch,
            setWaiting,
            hasActive,
            assessmentWindowStatus,
            students,
            schoolId,
            districtId,
            currentSchoolYear,
            controlledSelectedWindow,
            previousDistrictWindow,
          )}
        t={t}
        handleCancelScheduling={handleCancelScheduling} />
    </div>
    </>
  );
}


  export async function createAssignments(
    studentManifestConfig: StudentManifestConfig,
    studentManifestData: StudentData[],
    assignmentType: string,
    assignor: any, onCloseHandler: () => void,
    dispatch: AppDispatch,
    setWaiting: (waiting: boolean) => void,
    hasActive: boolean,
    assessmentWindowStatus: AssessmentWindow,
    students: string[],
    schoolId: string,
    districtId: string,
    currentSchoolYear?: number,
    controlledSelectedWindow?: SelectedWindow,
    previousDistrictWindow?: boolean,
    ) {
    try {
      setWaiting(true);
      const manifestLookup: Record<string, {manifest: any[], assignmentType: string}> = {};
      const studentBins: Record<string, string[]> = {};


      const clonedStudentData = studentManifestData.map(student => structuredClone(student));

      const transformedStudentData = clonedStudentData.map(student => {
        const config = studentManifestConfig[student.studentId];
        const languages = config.assessOn.split(', ');
        const sessions = config.sessions === 2 ? true : false;

        const isSpanishFirst = languages.length === 2 && languages[0] === "ES_MX";
        const isSpanishOnly = languages.length === 1 && languages[0] === "ES_MX";
        const isEnglishFirst = languages.length === 2 && languages[0] === "EN_US"
        const isEnglishOnly = languages.length === 1 && languages[0] === "EN_US"

        let newManifest;
        if (assignmentType === ASSIGNMENT_TYPE.PROGRESS_MONITORING && languages.length === 2) {
          const clonedManifest = structuredClone(student.manifest);

          let finalManifest;
          if (isSpanishFirst && sessions) {
            const spanishManifest = [{ "activityType": "assessment", "locale": "ES_MX", "sequence": "BREAK" }];
            const englishManifest = manifestHelper.transformManifest(clonedManifest, ["EN_US"], false);
            finalManifest = spanishManifest.concat(englishManifest);
            finalManifest = manifestHelper.insertBreak(finalManifest);
          } else if (isSpanishFirst && !sessions) {
            const spanishManifest = [{ "activityType": "assessment", "locale": "ES_MX", "sequence": "CONTINUE" }];
            const englishManifest = manifestHelper.transformManifest(clonedManifest, ["EN_US"], false);
            finalManifest = spanishManifest.concat(englishManifest);
          } else if (isEnglishFirst && sessions) {
            const englishManifest = manifestHelper.transformManifest(clonedManifest, ["EN_US"], false);
            const spanishManifest = [{ "activityType": "assessment", "locale": "ES_MX", "sequence": "CONTINUE" }];
            finalManifest = englishManifest.concat(spanishManifest);
            finalManifest = manifestHelper.insertBreak(finalManifest);
          } else if (isEnglishFirst && !sessions) {
            const englishManifest = manifestHelper.transformManifest(clonedManifest, ["EN_US"], false);
            const spanishManifest = [{ "activityType": "assessment", "locale": "ES_MX", "sequence": "CONTINUE" }];
            finalManifest = englishManifest.concat(spanishManifest);
          }

          return [{
            ...student,
            manifest: finalManifest,
            assignmentType: ASSIGNMENT_TYPE.PROGRESS_MONITORING
          }];
        }

        if (assignmentType === ASSIGNMENT_TYPE.BENCHMARK && languages.length === 2 && !hasActive) {
          const clonedManifest = structuredClone(student.manifest)
          const manifest = manifestHelper.transformManifest(clonedManifest, languages, sessions)

          return [{
            ...student,
            manifest: manifest,
            assignmentType: ASSIGNMENT_TYPE.BENCHMARK
          }];
        }

        if (isEnglishOnly && !hasActive) {
          newManifest = manifestHelper.transformManifest(structuredClone(student.manifest), ["EN_US"], false);
          return [{
            ...student,
            manifest: newManifest,
            assignmentType: assignmentType
          }];
        }

        if (isEnglishOnly && hasActive) {
          newManifest = manifestHelper.transformManifest(structuredClone(student.manifest), ["EN_US"], false);
          return [{
            ...student,
            manifest: newManifest,
            assignmentType: assignmentType
          }];
        }

        if (isSpanishOnly) {
          newManifest = assignmentType === ASSIGNMENT_TYPE.BENCHMARK
            ? manifestHelper.transformManifest(structuredClone(student.manifest), ["ES_MX"], false)
            : [{ "activityType": "assessment", "locale": "ES_MX", "sequence": "CONTINUE" }];

          return [{
            ...student,
            manifest: newManifest,
            assignmentType
          }];
        }

        if (assignmentType === ASSIGNMENT_TYPE.BENCHMARK && hasActive) {
          newManifest = manifestHelper.transformManifest(structuredClone(student.manifest), languages, sessions);
          return [{
            ...student,
            manifest: newManifest,
            assignmentType: ASSIGNMENT_TYPE.BENCHMARK
          }];
        }

        if (assignmentType === ASSIGNMENT_TYPE.PROGRESS_MONITORING && !student.edited && student.metadata?.esl) {
          newManifest = [
            { activityType: "assessment", locale: "EN_US", sequence: "BREAK" },
            { activityType: "assessment", locale: "ES_MX", sequence: "CONTINUE" },
          ];
        }

        return {
          ...student,
          manifest: newManifest || student.manifest
        };
      }).flat() as StudentDataWithAssignmentType[];


      let startDate: string;
      let endDate: string | null;

      if ((controlledSelectedWindow?.screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT || controlledSelectedWindow?.screeningWindowType === AssessmentPeriodWindowType.SCHOOL_ASSIGNMENT) && previousDistrictWindow) {

        startDate = controlledSelectedWindow.startDate!;
        endDate = null;
      } else {

        startDate = new Date().toISOString().split('T')[0];
        endDate = controlledSelectedWindow?.endDate || null;
      }

      for (const s of transformedStudentData) {
        const { manifest, studentId, assignmentType } = s;

        const manifestId = hash(manifest);

        manifestLookup[manifestId] = {
          manifest: manifest,
          assignmentType: assignmentType
        };
        if (!studentBins[manifestId]) studentBins[manifestId] = [];
        studentBins[manifestId].push(studentId);


        const isEsl = manifest.map((m: any) => m.locale).includes("ES_MX");
        if (isEsl) {
          const metadata = structuredClone(s.metadata);
          metadata.esl = true;
          SIS.updateStudentMetadata(s.studentId, metadata);
        }

        const config = studentManifestConfig[studentId];
        if (config?.instructionsLanguage === "ES_MX") {
          const metadata = structuredClone(s.metadata);
          metadata.tutorInSpanish = true;
          SIS.updateStudentMetadata(s.studentId, metadata);
        }
      }

      const successfulStudents = [];
      let assignmentIdPayload = controlledSelectedWindow?.assignmentId;

      for (const manifestId in studentBins) {
        const { manifest, assignmentType } = manifestLookup[manifestId];
        const payload: any = {
          entityIdList: studentBins[manifestId],
          assignmentType: assignmentType,
          manifest,
          assignor: assignor,
          dateFrom: startDate,
          dateTo: endDate,
          schoolYear: currentSchoolYear,
          periodId: controlledSelectedWindow?.value
        };
        if (controlledSelectedWindow?.screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT || 
            controlledSelectedWindow?.screeningWindowType === AssessmentPeriodWindowType.SCHOOL_ASSIGNMENT) {
          payload.parentId = assignmentIdPayload;
          
          // Inherit tags from district/school assignment when scheduling BOY/MOY/EOY benchmarks
          if (controlledSelectedWindow?.benchmarkTag && assessmentWindowStatus?.tags) {
                      payload.tags = assessmentWindowStatus.tags || [];
          }
        }

        const res = await addBatchAssignments(payload);

        const successfulIds = res.batchAddAssignment.map((assignment: any) => assignment.entityId);
        const successfulStudentsForBatch = studentManifestData.filter(student =>
          successfulIds.includes(student.studentId)
        );
        successfulStudents.push(...successfulStudentsForBatch);
      }

      const firstStudent = successfulStudents[0];
      const totalSuccessful = successfulStudents.length;
      const othersCount = totalSuccessful - 1;

      const toastMessage = othersCount > 0
        ? `Assessments for ${firstStudent.first_name} ${firstStudent.last_name.charAt(0)} and ${othersCount} others have been scheduled.`
        : totalSuccessful === 1
          ? `Assessment for ${firstStudent.first_name} ${firstStudent.last_name.charAt(0)} has been scheduled.`
          : 'No assessments were successfully scheduled. Please try again';

      if (totalSuccessful > 0) {
          makeToast(toastMessage, ToastTypes.success, true, 5000);
      }
    } catch (error) {
      console.error('Error scheduling assessments', error);
    } finally {
      setWaiting(false);
      onCloseHandler();
        document.body.classList.remove('modal-open');
        const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }
      dispatch(fetchAssessmentStatusWidgetData({
        entityIdList: students.map((student: any) => student.id),
        schoolId: Number(schoolId),
        districtId: Number(districtId),
        currentPeriodId: assessmentWindowStatus.currentPeriodIdentifier!,
        entityRoster: students,
        assessmentWindowStatus,
        currentWindowType: controlledSelectedWindow?.screeningWindowType || 'DistrictAssignment',
        selectedWindow: controlledSelectedWindow ? {
          filterType: (controlledSelectedWindow.screeningWindowType === 'DistrictAssignment' || controlledSelectedWindow.screeningWindowType === 'SchoolAssignment') ? 'BENCHMARK' as const : 'PERIOD' as const,
          benchmarkTag: (controlledSelectedWindow.screeningWindowType === 'DistrictAssignment' || controlledSelectedWindow.screeningWindowType === 'SchoolAssignment') ? 
            (() => {
              // Try to determine benchmark tag from assessment window status
              const tags = assessmentWindowStatus?.tags || [];
              if (tags.includes('BOY') || tags.includes('WINDOW_BOY')) return 'BOY' as const;
              if (tags.includes('MOY') || tags.includes('WINDOW_MOY')) return 'MOY' as const;
              if (tags.includes('EOY') || tags.includes('WINDOW_EOY')) return 'EOY' as const;
              return undefined;
            })() : undefined,
          value: controlledSelectedWindow.value
        } : undefined
      }));
    }
  }


function StudentList({ students, studentManifestConfig, setStudentManifestConfig, selectedStudents }: { students: any[], studentManifestConfig: any, setStudentManifestConfig: (config: any) => void, selectedStudents: any[] }) {
  const { t } = useTranslation("tracking_report");

  if (!studentManifestConfig || Object.keys(studentManifestConfig).length === 0) {
    return (
      <div className="">
        <div className="student-group-header">
          <div className="student-group-header-student-name">
            Student Name
            <img src={filterOutline} role="presentation" />
          </div>
          <div className="student-group-header-assess-on">
            Assess On
            <img src={filterOutline} role="presentation" />
          </div>
          <div className="student-group-header-sessions">
            Sessions
            <img src={filterOutline} role="presentation" />
          </div>
          <div className="student-group-header-instructions-language">
            Instructions Language
            <img src={filterOutline} role="presentation" />
          </div>
        </div>
        {Array(Object.keys(selectedStudents[0]).length).fill(0).map((_, index) => (
          <div className="loading-student-group" key={index}>
            <Placeholder heightInRem={4.5} animation="glow" colWidth={12}/>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="section">
      <div className="section-info">
        <div className="d-flex justify-content-center align-items-center">
        </div>
      </div>
      <div className="student-group-header">
        <div className="student-group-header-student-name">
          Student Name
          <img src={filterOutline} role="presentation" />
        </div>
        <div className="student-group-header-assess-on">
          Assess On
          <img src={filterOutline} role="presentation" />
        </div>
        <div className="student-group-header-sessions">
          Sessions
          <img src={filterOutline} role="presentation" />
        </div>
        <div className="student-group-header-instructions-language">
          Instructions Language
          <img src={filterOutline} role="presentation" />
        </div>
      </div>

      {studentGroups.map((group: any, i: number) => {
        const filtered = students.filter(group.filter);
        if (filtered.length === 0) return;
        return (
          <div className="student-group" key={i}>
            {filtered.map((student: any) => {
              const manifestConfig = studentManifestConfig[student.studentId];
              return (
                <StudentRow
                  data={student}
                  key={student.studentId}
                  manifestConfig={manifestConfig}
                  onChange={(manifestChanges: ManifestConfig) => {
                    setStudentManifestConfig((prevConfig: any) => ({
                      ...prevConfig,
                      [student.studentId]: manifestChanges,
                    }));
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function TaskList({ students, type, contentTags }: { students: any[], type: string, contentTags: any }) {
  const { t } = useTranslation("tracking_report");
  const types = [];

  if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING) {
    types.push(["assessment"]);
  } else {
    for (const s of students) {
      const { manifest, grade } = s;
      for (const m of manifest) {
        const { activityType } = m;
        if (activityType === "dyslexia") {
          if (type !== ASSIGNMENT_TYPE.CALIBRATION) {
            types.push(...getDyslexiaTasksByGrade(grade, contentTags));
          } else {
            types.push("calibration");
          }
        } else {
          types.push(activityType);
        }
      }
    }
  }

  return (
    <div className="section">
      <div className="section-info">
        <div className="section-label">{t("TASKS")}:</div>
        <div className="section-description">{t("tasks_description")}</div>
      </div>
      <div className="task-list">
        <div className="task-header">
          <div className="task-col-task">{t("Task")}</div>
          <div className="task-col-description">{t("Description")}</div>
          <div className="task-col-reading-rope-domains">{t("Reading Rope Domains")}</div>
        </div>
        {[...new Set(types)].map((type: TaskType, i: number) => {
          const task = taskLookup[type];
          if (!task) {
            return null;
          }
          const { domains } = task;
          return (
            <div key={i} className="task-row">
              <div className="task-row-task">
                <div className="task-row-name">
                  <div className="task-number">{i + 1}</div>
                  <div>{t(`${type}_name`)}</div>
                </div>
              </div>
              <div className="task-row-description">{t(`${type}_info`)}</div>
              <div className="task-row-reading-rope-domains">
                {domains.map((domain: any) => (
                  <DomainIcon type={domain} key={domain} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface LanguageOption {
  label: string;
  value: string;
}

function StudentRow({data, manifestConfig, onChange}: {data: any, manifestConfig: ManifestConfig, onChange: (manifestChanges: ManifestConfig) => void}) {
  const manifestData = data;
  const hasInitialized = useRef(false);

  const determineDefaultLanguage = (metadata: any) => {
    const { esl, assessmentLanguageOverride } = metadata || {};


    if (!esl) {
      return "EN_US";
    }


    if (esl) {
      switch (assessmentLanguageOverride) {
        case "ENGLISH_ONLY":
          return "EN_US";
        case "SPANISH_ONLY":
          return "ES_MX";
        case "BOTH_ONE_SESSION":
          return "EN_US, ES_MX";
        default:

          return "EN_US, ES_MX";
      }
    }


    return "EN_US";
  };

  const determineDefaultSessions = (metadata: any) => {
    const { esl, assessmentLanguageOverride } = metadata || {};


    const defaultLang = determineDefaultLanguage(metadata);


    if (esl && assessmentLanguageOverride === "BOTH_ONE_SESSION") {
      return 1;
    }


    if (defaultLang === "EN_US" || defaultLang === "ES_MX") {
      return 1;
    }


    return manifestConfig.sessions;
  };

  const determineDefaultInstructionsLanguage = (metadata: any) => {

    return metadata?.tutorInSpanish ? "ES_MX" : "EN_US";
  };


  const defaultLanguage = determineDefaultLanguage(manifestData.metadata);
  const defaultSessions = determineDefaultSessions(manifestData.metadata);
  const defaultInstructionsLanguage = determineDefaultInstructionsLanguage(manifestData.metadata);

  const [assessOn, setAssessOn] = useState(defaultLanguage);
  const [sessions, setSessions] = useState(defaultSessions);
  const [instructionsLanguage, setInstructionsLanguage] = useState(defaultInstructionsLanguage);


  useEffect(() => {
    if (!hasInitialized.current) {
      const needsUpdate =
        defaultLanguage !== manifestConfig.assessOn ||
        defaultSessions !== manifestConfig.sessions ||
        defaultInstructionsLanguage !== manifestConfig.instructionsLanguage;

      if (needsUpdate) {
        const updatedConfig = {
          ...manifestConfig,
          assessOn: defaultLanguage,
          sessions: defaultSessions,
          instructionsLanguage: defaultInstructionsLanguage
        };
        onChange(updatedConfig);
      }
      hasInitialized.current = true;
    }
  }, []);

  const { t } = useTranslation("tracking_report");

  let localesInManifest: string[] = manifestData.manifest.map((m: any) => m.locale);
  if(manifestData.metadata && manifestData.metadata.assessmentLanguageOverride){
    if(manifestData.metadata.assessmentLanguageOverride === "ENGLISH_ONLY"){
      localesInManifest = ["EN_US"];
    }else {
      if(manifestData.metadata.assessmentLanguageOverride === "SPANISH_ONLY"){
        localesInManifest = ["ES_MX"];
      }
    }
  }

  const sessionOptions = [
    { label: "1", value: 1 },
    { label: "2", value: 2 },
  ];

  const languageOptions: LanguageOption[] = [
    { label: "English then Spanish", value: ["EN_US", "ES_MX"].join(', ') as string },
    { label: "Spanish then English", value: ["ES_MX", "EN_US"].join(', ') as string },
    { label: "English", value: ["EN_US"].join(', ') as string },
    { label: "Spanish", value: ["ES_MX"].join(', ') as string },
  ];

  const instructionalLanguageOptions = [
    { label: "English", value: "EN_US" },
    { label: "Spanish", value: "ES_MX" },
  ];

  const handleDropdownChange = (option: any) => {
    setAssessOn(option.value);
    const isSingleLanguage = option.value === "EN_US" || option.value === "ES_MX";

    const updatedConfig = {
      ...manifestConfig,
      assessOn: option.value,
      sessions: isSingleLanguage ? 1 : manifestConfig.sessions
    };

    setAssessOn(option.value);
    setSessions(isSingleLanguage ? 1 : manifestConfig.sessions);
    onChange(updatedConfig);
  };

  const handleSessionDropdownChange = (option: any) => {
    setSessions(option.value);
    const updatedConfig = {
      ...manifestConfig,
      sessions: option.value
    };
    onChange(updatedConfig);
  };

  const handleLanguageDropdownChange = (option: any) => {
    setInstructionsLanguage(option.value);
    const updatedConfig = {
      ...manifestConfig,
      instructionsLanguage: option.value
    };
    onChange(updatedConfig);
  };

  const assessLanguage = languageOptions.find(option => option.value === assessOn)?.label
  const instructionalLanguage = instructionalLanguageOptions.find(option => option.value === instructionsLanguage)?.label

  return (
    <div className="student">
      <div className="student-name">
        <div className="student-name-content">
          <StudentAvatar firstName={data.first_name} lastName={data.last_name} />
          <span className="student-name-content-text">{`${data.first_name} ${data.last_name}`}</span>
        </div>
      </div>
      <div className="student-locale-container">
        <Dropdown
          options={languageOptions}
          onChange={handleDropdownChange}
          styles={{display: 'flex', minWidth: '13.6875rem', padding: '0', alignItems: 'center', gap: '0.5rem', flex: '1 0 0', color: 'black'}}
          type="primary"
          placeholder="Assess Language"
          isDisabled={false}
          value={{ label: assessLanguage, value: assessLanguage }}
          aria-label="Language Option"
          menuPosition="fixed"
        />
      </div>
      <div className="student-session-container">
        <Dropdown
          options={sessionOptions}
          onChange={handleSessionDropdownChange}
          styles={{display: 'flex', minWidth: '3.85rem', padding: '0.125rem 0rem 0.125rem 0.5rem', alignItems: 'center', gap: '0.5rem', flex: '1 0 0', color: 'black'}}
          type="primary"
          placeholder={sessions.toString()}
          isDisabled={assessOn === "EN_US" || assessOn === "ES_MX"}
          value={{ label: sessions, value: sessions}}
          aria-label="Session Count"
          menuPosition="fixed"
        />
      </div>
      <div className="student-language-container">
        <Dropdown
          options={instructionalLanguageOptions}
          onChange={handleLanguageDropdownChange}
          styles={{display: 'flex', minWidth: '7.125rem', padding: '0.125rem 0.5rem', alignItems: 'center', gap: '0.5rem', flex: '1 0 0', color: 'black'}}
          type="primary"
          placeholder="Instructional Language"
          isDisabled={false}
          value={{ label: instructionalLanguage, value: instructionsLanguage }}
          aria-label="Instructional Language Option"
          menuPosition="fixed"
        />
      </div>
    </div>
  );
}

const studentGroups = [
  {
    label: "English Only",
    filter: (s: any) => {
      const locales = s.manifest.map((m: any) => m.locale);
      return locales.includes("EN_US") && !locales.includes("ES_MX");
    },
  },
  {
    label: "Spanish Only",
    filter: (s: any) => {
      const locales = s.manifest.map((m: any) => m.locale);
      return !locales.includes("EN_US") && locales.includes("ES_MX");
    },
  },
  {
    label: "English & Spanish",
    filter: (s: any) => {
      const locales = s.manifest.map((m: any) => m.locale);
      return locales.includes("EN_US") && locales.includes("ES_MX");
    },
  },
];

const taskLookup = {
  assessment: {
    domains: ["decoding", "phonemic", "recognition", "comprehension"],
  },
  nwf: {
    domains: ["dyslexia", "decoding"],
  },
  spelling: {
    domains: ["dyslexia", "decoding"],
  },
  listeningComprehension: {
    domains: ["dyslexia", "comprehension"],
  },
  vocabularyScreener: {
    domains: ["dyslexia", "comprehension"],
  },
  readingComprehension: {
    domains: ["comprehension"],
  },
  ran: {
    domains: ["dyslexia"],
  },
  letterSounds: {
    domains: ["dyslexia", "decoding"],
  },
  wordReading: {
    domains: ["dyslexia", "decoding"],
  },
  blending: {
    domains: ["dyslexia", "phonemic"],
  },
  calibration: {
    domains: ["dyslexia", "decoding"],
  },
  wordManipulation: {
    domains: ["dyslexia", "phonemic"],
  },
  nonsenseWordReplication: {
    domains: ["dyslexia", "phonemic"],
  },
  letterNamingAndSounds: {
    domains: ["dyslexia", "decoding"],
  },
};

export const getDyslexiaTasksByGrade = (grade: any, contentTags: any) => {
  const defaultTasks = getAmiraDefaultDyslexiaTasks(grade);
  const overriddenTaskValuesByTag = applySavedConfigurationTagOverrides(contentTags, defaultTasks, grade);
  return overriddenTaskValuesByTag;
};

/**
 * Get the default dyslexia tasks for a given grade
 * @param {Number} grade
 * @returns {Array} - an array of task objects that should be included in the dyslexia tasks
 */
export const getAmiraDefaultDyslexiaTasks = (grade: any) => {
  const tasks = CONFIGURATION_DATA.reduce((acc: { name: string; value: string; }[], config) => {
    if (DYSLEXIA_TASKS.filter(task => task.name === config.name).length) {
      const gradeConfig = config.configuration[grade];
      if (gradeConfig && typeof gradeConfig === "object") {
        if (gradeConfig.value === true) {
          const task = DYSLEXIA_TASKS.find((task: any) => task.name === config.name);
          if (task) {
            acc.push(task);
          }
        }
      }
    }
    return acc;
  }, [] as { name: string; value: string; }[]);

  return tasks;
};

/**
 * Use the saved assessment tag to determine if there are any task overrides for the dyslexia tasks
 * if there is, return the task values that should be included in the dyslexia tasks after applying the overrides
 * @param {Array} contentTags
 * @param {Array} defaultTasks
 * @param {Number} gradeIndex
 * @returns {Array} - an array of task values that should be included in the dyslexia tasks
 */
export const applySavedConfigurationTagOverrides = (
  contentTags: any,
  defaultTasks: any,
  gradeIndex: any
) => {
  const overrides = (findSelectedAssessmentTagObjectFromSavedTags(contentTags) as AssessmentTagObject)?.override;
  if (!overrides) {
    return defaultTasks.map((task: any) => task.value);
  }

  return defaultTasks.reduce((acc: any, task: any) => {
      if(!overrides?.[task.name]){
        acc.push(task.value);
      }else if(overrides?.[task.name]?.[gradeIndex] === 1){
        acc.push(task.value);
      }
      return acc;
  }, []);
}